import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Puzzle, MessageSquare, BookOpen } from 'lucide-react';

export const MissionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  const missionTypes = [
    {
      icon: Puzzle,
      title: t('missions.expertise.title'),
      description: t('missions.expertise.description'),
      color: "primary"
    },
    {
      icon: MessageSquare,
      title: t('missions.mentoring.title'),
      description: t('missions.mentoring.description'),
      color: "accent"
    },
    {
      icon: BookOpen,
      title: t('missions.knowledge.title'),
      description: t('missions.knowledge.description'),
      color: "primary"
    }
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 section-cream">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
            {t('missions.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('missions.subtitle')}
          </p>
        </motion.div>

        {/* Mission Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {missionTypes.map((mission, index) => {
            const IconComponent = mission.icon;
            
            return (
              <motion.div
                key={mission.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                className="group"
              >
                <div className="bg-card rounded-2xl p-8 h-full border border-border/50 hover:border-primary/30 hover:shadow-medium transition-all duration-300 flex flex-col">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <IconComponent size={28} className="text-primary" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
                    {mission.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed flex-1">
                    {mission.description}
                  </p>

                  {/* Decorative Line */}
                  <div className="mt-6 pt-6 border-t border-border/50">
                    <div className="w-12 h-1 bg-gradient-gold rounded-full group-hover:w-20 transition-all duration-300" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
